import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Play, Search, Download, Eye, CheckCircle, Check,
  Loader2, IndianRupee, FileText, Calculator, Building2,
  TrendingUp, Shield, Clock, Users, BarChart3, ChevronDown,
  XCircle, Banknote, Printer, MessageSquare, RotateCcw, UserX, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, PieChart, Pie, Cell
} from 'recharts';

import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';
import { PayrollReviewSheet } from '../components/PayrollReviewSheet';
import { PayrollVisibility } from '../components/PayrollVisibility';
import { MultiSelect } from '../components/MultiSelect';
import { ClaSettingsDialog } from '../components/ClaSettingsDialog';
import { fullName } from '../lib/fullName';
const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const formatMonth = (month) =>
  new Date(month + '-01').toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

const CHART_COLORS = ['#DC2626', '#2563EB', '#16A34A', '#D97706', '#7C3AED', '#0891B2'];

const getHeaders = () => {
  const token = localStorage.getItem('hrms-token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// ========== PAYROLL RECORDS TAB ==========
function PayrollRecordsTab({ selectedEntity, employees, isEmployee, myEmployeeId, canGeneratePayroll, canApprovePayroll, isFinance, canEditPayroll, monthFilter, setMonthFilter }) {
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(null);
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [templateEdit, setTemplateEdit] = useState(null);
  const [templateEditInputs, setTemplateEditInputs] = useState({});
  const [templatePersist, setTemplatePersist] = useState(false);
  const [savingTemplateEdit, setSavingTemplateEdit] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('all');
  const [bulkChecked, setBulkChecked] = useState({});
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtCategories, setStmtCategories] = useState([]);
  const [stmtDepts, setStmtDepts] = useState([]);
  const [stmtApptTypes, setStmtApptTypes] = useState([]);
  const [stmtChecked, setStmtChecked] = useState({});
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyChecked, setApplyChecked] = useState({});
  const [applying, setApplying] = useState(false);
  const [formData, setFormData] = useState({ employee_id: '', month: monthFilter });
  const [viewMode, setViewMode] = useState('sheet');
  const [sheetLocked, setSheetLocked] = useState(false);
  // Template-wise salary view inside Records: 'default' = standard sheet; else an Excel template id
  const [selectedTemplate, setSelectedTemplate] = useState('default');
  const [templateView, setTemplateView] = useState(null);
  const [templateViewLoading, setTemplateViewLoading] = useState(false);
  const [tmplDownloading, setTmplDownloading] = useState(false);

  const showTemplatePicker = !isEmployee && (canApprovePayroll || canEditPayroll || canGeneratePayroll);
  const tmoney = (v) => (typeof v === 'number' ? v.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : (v ?? '—'));

  // Template-wise scoping: which staff belong to each template, and the staff the
  // currently selected option (Default vs a specific template) should act on.
  const coveredIds = useMemo(() => {
    const s = new Set();
    (templateView?.templates || []).forEach(t => (t.rows || []).forEach(r => s.add(r.employee_id)));
    return s;
  }, [templateView]);
  const templateEmpIds = useMemo(() => {
    const m = {};
    (templateView?.templates || []).forEach(t => { m[t.template_id] = new Set((t.rows || []).map(r => r.employee_id)); });
    return m;
  }, [templateView]);
  const scopedEmployees = useMemo(() => {
    if (selectedTemplate === 'default') return employees.filter(e => !coveredIds.has(e.employee_id));
    const ids = templateEmpIds[selectedTemplate];
    return ids ? employees.filter(e => ids.has(e.employee_id)) : [];
  }, [employees, coveredIds, templateEmpIds, selectedTemplate]);
  const selectedTemplateName = selectedTemplate === 'default'
    ? 'Default (standard salary)'
    : ((templateView?.templates || []).find(t => t.template_id === selectedTemplate)?.name || 'template');

  // Load the month's template-wise sheets so the Records view can switch between the
  // standard (Default) sheet and each custom Excel template (e.g. Gurumauli).
  useEffect(() => {
    if (!showTemplatePicker || !monthFilter) { setTemplateView(null); return; }
    let cancelled = false;
    (async () => {
      setTemplateViewLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/salary-excel-templates/payroll-view?month=${encodeURIComponent(monthFilter)}&mode=payroll`,
          { headers: getHeaders(), credentials: 'include' });
        const d = res.ok ? await res.json() : { templates: [] };
        if (!cancelled) setTemplateView(d && !d.detail ? d : { templates: [] });
      } catch { if (!cancelled) setTemplateView({ templates: [] }); }
      finally { if (!cancelled) setTemplateViewLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [monthFilter, showTemplatePicker]);

  // If the chosen template no longer covers anyone (e.g. month changed), fall back to Default.
  useEffect(() => {
    if (selectedTemplate !== 'default' && templateView &&
        !(templateView.templates || []).some(t => t.template_id === selectedTemplate)) {
      setSelectedTemplate('default');
    }
  }, [templateView, selectedTemplate]);

  const downloadTemplate = async (tmpl) => {
    setTmplDownloading(true);
    try {
      const res = await fetch(`${API_URL}/api/salary-excel-templates/${tmpl.template_id}/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getHeaders() },
        credentials: 'include', body: JSON.stringify({ month: monthFilter }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `Failed (HTTP ${res.status})`); }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(tmpl.name || 'salary').replace(/[^a-zA-Z0-9]+/g, '_')}_${monthFilter}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
      toast.success('Salary file downloaded');
    } catch (e) { toast.error(e.message); } finally { setTmplDownloading(false); }
  };

  // Filter the staff available to the generate controls (scoped to the selected template).
  const filterByCategory = useCallback((cat) => scopedEmployees.filter(e =>
    cat === 'all' || (e.category || '').trim().toLowerCase() === cat.toLowerCase()
  ), [scopedEmployees]);

  const categoryOptions = useMemo(() => Array.from(
    new Set(employees.map(e => (e.category || '').trim()).filter(Boolean))
  ).sort(), [employees]);
  const departmentOptions = useMemo(() => Array.from(
    new Set(employees.map(e => (e.department || '').trim()).filter(Boolean))
  ).sort(), [employees]);
  const appointmentTypeOptions = useMemo(() => Array.from(
    new Set(employees.map(e => (e.appointment_type || '').trim()).filter(Boolean))
  ).sort(), [employees]);

  // Multi-filter: an empty selection array means "none"; callers default to the full list.
  // Order is preserved from `employees` (already sorted by master-data display_order).
  const filterStatementList = useCallback((cats, depts, appts) => {
    const catSet = new Set((cats || []).map(c => c.toLowerCase()));
    const deptSet = new Set((depts || []).map(d => d.toLowerCase()));
    const apptSet = new Set((appts || []).map(a => a.toLowerCase()));
    return employees.filter(e =>
      catSet.has((e.category || '').trim().toLowerCase()) &&
      deptSet.has((e.department || '').trim().toLowerCase()) &&
      apptSet.has((e.appointment_type || '').trim().toLowerCase())
    );
  }, [employees]);

  // Default: select all matching employees whenever the dialogs open or category changes
  useEffect(() => {
    if (isBulkDialogOpen) {
      const m = {};
      filterByCategory(bulkCategory).forEach(e => { m[e.employee_id] = true; });
      setBulkChecked(m);
    }
  }, [isBulkDialogOpen, bulkCategory, filterByCategory]);
  useEffect(() => {
    if (stmtOpen) {
      const cats = categoryOptions;
      const depts = departmentOptions;
      const appts = appointmentTypeOptions;
      setStmtCategories(cats);
      setStmtDepts(depts);
      setStmtApptTypes(appts);
      const m = {};
      filterStatementList(cats, depts, appts).forEach(e => { m[e.employee_id] = true; });
      setStmtChecked(m);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stmtOpen]);

  // Re-apply the "select all matching" default whenever the filters change inside the dialog.
  useEffect(() => {
    if (stmtOpen) {
      const m = {};
      filterStatementList(stmtCategories, stmtDepts, stmtApptTypes).forEach(e => { m[e.employee_id] = true; });
      setStmtChecked(m);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stmtCategories, stmtDepts, stmtApptTypes]);
  useEffect(() => {
    if (applyOpen) {
      const m = {};
      payrollRecords.forEach(p => { m[p.employee_id] = true; });
      setApplyChecked(m);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyOpen]);

  const handleBulkApplyToMaster = async () => {
    const ids = Object.keys(applyChecked).filter(k => applyChecked[k]);
    if (ids.length === 0) { toast.error('Select at least one employee'); return; }
    setApplying(true);
    try {
      const res = await fetch(`${API_URL}/api/payroll/persist-to-master?entity_id=${selectedEntity.entity_id}&month=${monthFilter}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getHeaders() },
        body: JSON.stringify({ employee_ids: ids }),
        credentials: 'include'
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed to apply values');
      toast.success(d.message || `Applied to master for ${d.updated} employees`);
      setApplyOpen(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setApplying(false);
    }
  };

  const fetchPayroll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedEntity) { params.append('entity_id', selectedEntity.entity_id); params.append('include_descendants', 'true'); }
      if (monthFilter) params.append('month', monthFilter);
      const res = await fetch(`${API_URL}/api/payroll?${params}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) setPayrollRecords(await res.json());
    } catch (err) {
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, [selectedEntity, monthFilter]);

  useEffect(() => { fetchPayroll(); }, [fetchPayroll]);

  // Open the right edit dialog: template-covered staff edit template-wise, others use the default form.
  const openEditPayroll = async (pay) => {
    let covered = false;
    try {
      const res = await fetch(`${API_URL}/api/salary-excel-templates/payroll-edit/${pay.payroll_id}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.covered) {
          const inputs = {};
          (data.columns || []).forEach(c => { if (c.editable) inputs[c.field_key] = c.input ?? 0; });
          setTemplateEditInputs(inputs);
          setTemplatePersist(false);
          setTemplateEdit({ pay, ...data });
          covered = true;
        }
      }
    } catch { /* fall back to default form */ }
    if (!covered) setEditingPayroll({ ...pay });
  };

  const submitTemplateEdit = async (save) => {
    if (!templateEdit) return;
    setSavingTemplateEdit(true);
    try {
      const res = await fetch(`${API_URL}/api/salary-excel-templates/payroll-edit/${templateEdit.pay.payroll_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getHeaders() },
        body: JSON.stringify({ overrides: templateEditInputs, dry_run: !save, persist_to_master: save && templatePersist }),
        credentials: 'include'
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'Failed');
      if (save) {
        toast.success(data.persisted?.length ? `Saved + ${data.persisted.length} field(s) to Employee Master` : 'Template values saved for this month');
        setTemplateEdit(null);
        fetchPayroll();
      } else {
        const inputs = {};
        (data.columns || []).forEach(c => { if (c.editable) inputs[c.field_key] = c.input ?? 0; });
        setTemplateEditInputs(inputs);
        setTemplateEdit(prev => ({ ...prev, columns: data.columns }));
        toast.success('Recalculated with your figures');
      }
    } catch (err) { toast.error(err.message); }
    finally { setSavingTemplateEdit(false); }
  };

  // Keep sheetLocked accurate in the Records view too (not only the Sheet view, where
  // PayrollReviewSheet emits it). Prevents the Delete-All button showing on a locked month.
  useEffect(() => {
    if (!canEditPayroll || !selectedEntity?.entity_id || !monthFilter) { return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/payroll/sheet-lock?entity_id=${selectedEntity.entity_id}&month=${monthFilter}`, { headers: getHeaders(), credentials: 'include' });
        if (res.ok && !cancelled) { const d = await res.json(); setSheetLocked(!!d.locked); }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [canEditPayroll, selectedEntity?.entity_id, monthFilter]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const emp = employees.find(e => e.employee_id === formData.employee_id);
      const res = await fetch(`${API_URL}/api/payroll/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getHeaders() },
        body: JSON.stringify({ employee_id: formData.employee_id, entity_id: emp?.entity_id || selectedEntity?.entity_id, month: formData.month }),
        credentials: 'include'
      });
      if (res.ok) { toast.success('Payroll generated'); setIsDialogOpen(false); fetchPayroll(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to generate payroll'); }
    finally { setGenerating(false); }
  };

  const handleBulkGenerate = async () => {
    if (!selectedEntity) { toast.error('Select an institute first'); return; }
    const selectedIds = Object.keys(bulkChecked).filter(k => bulkChecked[k]);
    if (selectedIds.length === 0) { toast.error('Select at least one employee'); return; }
    setGenerating(true);
    try {
      const catParam = bulkCategory && bulkCategory !== 'all' ? `&category=${encodeURIComponent(bulkCategory)}` : '';
      const res = await fetch(`${API_URL}/api/payroll/generate-bulk?entity_id=${selectedEntity.entity_id}&month=${monthFilter}&include_descendants=true${catParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getHeaders() },
        body: JSON.stringify({ employee_ids: selectedIds }),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        let msg = `Generated: ${data.generated}, Skipped: ${data.skipped}`;
        if (data.auto_added?.length) msg += ` · Late/left staff added: ${data.auto_added.length}`;
        toast.success(msg);
        if (data.errors?.length) {
          toast.error(`${data.errors.length} not generated: ${data.errors.slice(0, 3).join('; ')}`, { duration: 9000 });
        }
        setIsBulkDialogOpen(false);
        fetchPayroll();
      } else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to generate bulk payroll'); }
    finally { setGenerating(false); }
  };

  const handleBulkDelete = async () => {
    const recs = groupRecords;
    if (!recs.length) { toast.error('No records to delete'); return; }
    const label = selectedTemplate === 'default' ? (selectedEntity?.name || 'this institute') : selectedTemplateName;
    if (!window.confirm(`Delete ALL ${recs.length} generated payroll record(s) for ${label} in ${formatMonth(monthFilter)}?\n\nThis cannot be undone. You can re-generate afterwards.`)) return;
    try {
      let deleted = 0, failed = 0;
      for (const p of recs) {
        const res = await fetch(`${API_URL}/api/payroll/${p.payroll_id}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' });
        if (res.ok) deleted++; else failed++;
      }
      if (deleted) toast.success(`Deleted ${deleted} record(s)`);
      if (failed) toast.error(`${failed} record(s) could not be deleted`);
      fetchPayroll();
    } catch { toast.error('Failed to delete payroll'); }
  };

  const handleVerify = async (payrollId) => {
    try {
      const res = await fetch(`${API_URL}/api/payroll/${payrollId}/verify`, {
        method: 'PUT', headers: getHeaders(), credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { toast.success('Payroll verified'); fetchPayroll(); }
      else toast.error(data.detail || 'Failed to verify');
    } catch { toast.error('Failed to verify'); }
  };

  const handleApprove = async (payrollId) => {
    try {
      const res = await fetch(`${API_URL}/api/payroll/${payrollId}/approve`, {
        method: 'PUT', headers: getHeaders(), credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { toast.success('Payroll approved — employee can now view their report'); fetchPayroll(); }
      else toast.error(data.detail || 'Failed to approve');
    } catch { toast.error('Failed to approve'); }
  };

  const handleFinanceVerify = async (payrollId) => {
    try {
      const res = await fetch(`${API_URL}/api/payroll/${payrollId}/finance-review?action=verify`, {
        method: 'PUT', headers: getHeaders(), credentials: 'include'
      });
      if (res.ok) { toast.success('Payroll finalized'); fetchPayroll(); }
      else toast.error('Failed to verify');
    } catch { toast.error('Failed to verify'); }
  };

  const handleFinanceReject = async (payrollId) => {
    try {
      const res = await fetch(`${API_URL}/api/payroll/${payrollId}/finance-review?action=reject&comment=${encodeURIComponent(rejectComment)}`, {
        method: 'PUT', headers: getHeaders(), credentials: 'include'
      });
      if (res.ok) { toast.success('Sent back to HR for corrections'); setShowRejectDialog(null); setRejectComment(''); fetchPayroll(); }
      else toast.error('Failed to reject');
    } catch { toast.error('Failed to reject'); }
  };

  const handleMarkPaid = async (payrollId) => {
    try {
      const res = await fetch(`${API_URL}/api/payroll/${payrollId}/mark-paid`, {
        method: 'PUT', headers: getHeaders(), credentials: 'include'
      });
      if (res.ok) { toast.success('Marked as paid'); fetchPayroll(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to mark as paid'); }
  };

  const handleBankExport = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedEntity) params.append('entity_id', selectedEntity.entity_id);
      if (monthFilter) params.append('month', monthFilter);
      const res = await fetch(`${API_URL}/api/payroll/bank-export?${params}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `bank_payment_${monthFilter}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
        toast.success('Bank details exported');
      } else toast.error('Failed to export');
    } catch { toast.error('Export failed'); }
  };

  const handleSalaryStatementDownload = async (employeeIds, title) => {
    if (!selectedEntity?.entity_id) {
      toast.error('Please pick a specific institute (not the trust root) before downloading the statement.');
      return;
    }
    if (!monthFilter) {
      toast.error('Please pick a month first.');
      return;
    }
    try {
      const params = new URLSearchParams({
        entity_id: selectedEntity.entity_id, month: monthFilter, include_descendants: 'true',
      });
      if (title) params.append('title', title);
      if (employeeIds && employeeIds.length > 0) params.append('employee_ids', employeeIds.join(','));
      const res = await fetch(`${API_URL}/api/salary-reports/salary-statement?${params}`, { headers: getHeaders(), credentials: 'include' });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Failed (HTTP ${res.status})`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const label = (title || 'ALL').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '');
      a.download = `salary_statement_${label}_${monthFilter}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
      toast.success('Salary statement downloaded');
    } catch (e) {
      toast.error(e.message || 'Failed to download statement');
    }
  };

  const [reportsOpen, setReportsOpen] = useState(false);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [coverageQ, setCoverageQ] = useState('');
  const [coverageLoading, setCoverageLoading] = useState(false);
  const fetchCoverage = async () => {
    if (!selectedEntity?.entity_id) { toast.error('Select an institute first'); return; }
    setCoverageLoading(true);
    try {
      const params = new URLSearchParams({ entity_id: selectedEntity.entity_id, month: monthFilter, include_descendants: 'true' });
      if (coverageQ.trim()) params.append('q', coverageQ.trim());
      const res = await fetch(`${API_URL}/api/payroll/coverage?${params}`, { headers: getHeaders(), credentials: 'include' });
      const data = await res.json();
      if (res.ok) setCoverage(data); else toast.error(data.detail || 'Failed to load coverage');
    } catch { toast.error('Failed to load coverage'); }
    finally { setCoverageLoading(false); }
  };
  const downloadReportFile = async (path, extraParams, baseName) => {
    if (!selectedEntity?.entity_id) { toast.error('Pick a specific institute first.'); return; }
    if (!monthFilter) { toast.error('Pick a month first.'); return; }
    try {
      const params = new URLSearchParams({ entity_id: selectedEntity.entity_id, month: monthFilter, ...(extraParams || {}) });
      const res = await fetch(`${API_URL}/api/salary-reports/${path}?${params}`, { headers: getHeaders(), credentials: 'include' });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Failed (HTTP ${res.status})`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${baseName}_${monthFilter}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (e) { toast.error(e.message || 'Failed to download report'); }
  };

  const downloadSlip = async (pay) => {
    try {
      const res = await fetch(`${API_URL}/api/payroll/${pay.payroll_id}/slip`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `salary_slip_${pay.month}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
        toast.success('Salary slip downloaded');
      } else toast.error('Failed to download');
    } catch { toast.error('Failed to download'); }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.employee_id === empId);
    return emp ? fullName(emp) : empId;
  };

  const filtered = payrollRecords.filter(pay => {
    if (isEmployee && myEmployeeId && pay.employee_id !== myEmployeeId) return false;
    const name = getEmployeeName(pay.employee_id).toLowerCase();
    return name.includes(searchTerm.toLowerCase()) || pay.employee_id.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Records belonging to the currently selected option (Default = non-template staff;
  // a template = only that template's staff). Stats, Records table and Delete All all use this
  // so each option behaves as its own payroll group.
  const groupRecords = selectedTemplate === 'default'
    ? filtered.filter(p => !coveredIds.has(p.employee_id))
    : filtered.filter(p => (templateEmpIds[selectedTemplate] || new Set()).has(p.employee_id));
  const totalGross = groupRecords.reduce((s, p) => s + (p.gross_salary || 0), 0);
  const totalNet = groupRecords.reduce((s, p) => s + (p.net_salary || 0), 0);
  const totalDeductions = groupRecords.reduce((s, p) => s + (p.total_deductions || 0), 0);
  const selectedTemplateObj = (templateView?.templates || []).find(t => t.template_id === selectedTemplate) || null;

  return (
    <div className="space-y-5">
      {/* Stats — shown for both Default and each template, scoped to that group's records */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="stat-records"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Records</p><p className="text-2xl font-bold mt-1">{groupRecords.length}</p></div><FileText className="w-8 h-8 text-slate-400" /></div></CardContent></Card>
        {!isEmployee && (
          <>
            <Card data-testid="stat-gross"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Gross Salary</p><p className="text-xl font-bold mt-1">{formatCurrency(totalGross)}</p></div><IndianRupee className="w-8 h-8 text-emerald-500" /></div></CardContent></Card>
            <Card data-testid="stat-deductions"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Deductions</p><p className="text-xl font-bold mt-1 text-red-600">{formatCurrency(totalDeductions)}</p></div><TrendingUp className="w-8 h-8 text-red-400" /></div></CardContent></Card>
            <Card data-testid="stat-net"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Net Payable</p><p className="text-xl font-bold mt-1 text-emerald-600">{formatCurrency(totalNet)}</p></div><CheckCircle className="w-8 h-8 text-emerald-500" /></div></CardContent></Card>
          </>
        )}
      </div>

      {/* Filters + Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:flex-wrap gap-3 items-stretch md:items-end">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search employee..." className="pl-10" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="payroll-search" />
            </div>
            <Input type="month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)} className="w-full md:w-[180px]" data-testid="month-filter" />
            {showTemplatePicker && (templateView?.templates?.length > 0) && (
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger className="w-full md:w-[210px] shrink-0" data-testid="payroll-template-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default (standard salary)</SelectItem>
                  {(templateView?.templates || []).map(t => (
                    <SelectItem key={t.template_id} value={t.template_id} data-testid={`payroll-template-opt-${t.template_id}`}>
                      {t.name} ({t.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {!isEmployee && (
              <div className="flex rounded-md border overflow-hidden shrink-0">
                <button type="button" onClick={() => setViewMode('sheet')} data-testid="view-mode-sheet"
                  className={`px-3 py-2 text-xs font-medium transition-colors ${viewMode === 'sheet' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                  Review Sheet
                </button>
                <button type="button" onClick={() => setViewMode('records')} data-testid="view-mode-records"
                  className={`px-3 py-2 text-xs font-medium transition-colors ${viewMode === 'records' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                  Records
                </button>
              </div>
            )}
            {canGeneratePayroll && (
              <div className="flex gap-2">
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="generate-payroll-btn"><Play className="w-4 h-4 mr-1.5" />Single</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Generate Payroll</DialogTitle></DialogHeader>
                    <form onSubmit={handleGenerate} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Employee</Label>
                        <Select value={formData.employee_id} onValueChange={v => setFormData({...formData, employee_id: v})}>
                          <SelectTrigger data-testid="select-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                          <SelectContent>{scopedEmployees.map(emp => (<SelectItem key={emp.employee_id} value={emp.employee_id}>{fullName(emp)} - {formatCurrency(emp.basic_salary)}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Month</Label>
                        <Input type="month" value={formData.month} onChange={e => setFormData({...formData, month: e.target.value})} data-testid="payroll-month" />
                      </div>
                      <div className="flex justify-end gap-3 pt-2">
                        <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                        <Button type="submit" className="btn-primary" disabled={generating} data-testid="submit-payroll-btn">
                          {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Calculator className="w-4 h-4 mr-1.5" />}Generate
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="bulk-generate-btn"><Users className="w-4 h-4 mr-1.5" />Bulk</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Bulk Generate Payroll</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-4">
                      <p className="text-sm text-slate-600">Generate payroll for <strong>{selectedTemplateName}</strong> staff in <strong>{selectedEntity?.name || 'selected institute'}</strong> for <strong>{formatMonth(monthFilter)}</strong>.</p>
                      <div className="space-y-1.5">
                        <Label>Staff Category</Label>
                        <Select value={bulkCategory} onValueChange={setBulkCategory}>
                          <SelectTrigger data-testid="bulk-category-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Staff</SelectItem>
                            <SelectItem value="Teaching">Teaching</SelectItem>
                            <SelectItem value="Non-Teaching">Non-Teaching</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {(() => {
                        const list = filterByCategory(bulkCategory);
                        const selCount = list.filter(e => bulkChecked[e.employee_id]).length;
                        const allSel = list.length > 0 && selCount === list.length;
                        return (
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <Label className="flex items-center gap-2">
                                <input type="checkbox" checked={allSel}
                                  onChange={(e) => {
                                    const m = { ...bulkChecked };
                                    list.forEach(emp => { m[emp.employee_id] = e.target.checked; });
                                    setBulkChecked(m);
                                  }}
                                  data-testid="bulk-select-all" />
                                Select All ({selCount}/{list.length})
                              </Label>
                            </div>
                            <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1" data-testid="bulk-employee-list">
                              {list.map(emp => (
                                <label key={emp.employee_id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer hover:bg-slate-50 rounded px-1">
                                  <input type="checkbox" checked={!!bulkChecked[emp.employee_id]}
                                    onChange={(e) => setBulkChecked({ ...bulkChecked, [emp.employee_id]: e.target.checked })}
                                    data-testid={`bulk-emp-${emp.employee_id}`} />
                                  <span className="flex-1">{fullName(emp)}</span>
                                  <span className="text-xs text-slate-400">{emp.category || '—'}</span>
                                </label>
                              ))}
                              {list.length === 0 && <p className="text-xs text-slate-500 text-center py-2">No employees in this category</p>}
                            </div>
                          </div>
                        );
                      })()}
                      <p className="text-xs text-amber-600">Employees with existing payroll for this month will be skipped.</p>
                      <div className="flex justify-end gap-3 pt-2">
                        <Button variant="outline" onClick={() => setIsBulkDialogOpen(false)}>Cancel</Button>
                        <Button className="btn-primary" onClick={handleBulkGenerate} disabled={generating} data-testid="confirm-bulk-btn">
                          {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Play className="w-4 h-4 mr-1.5" />}Generate All
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            )}
            {isFinance && sheetLocked && selectedTemplate === 'default' && (
              <Button variant="outline" onClick={handleBankExport} data-testid="bank-export-btn">
                <Download className="w-4 h-4 mr-1.5" />Bank Export
              </Button>
            )}
            {(canApprovePayroll || canEditPayroll) && (selectedTemplate === 'default' ? (sheetLocked || groupRecords.length > 0) : (selectedTemplateObj && selectedTemplateObj.count > 0)) && (
              <Button variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                onClick={() => { if (selectedTemplate === 'default') setStmtOpen(true); else if (selectedTemplateObj) downloadTemplate(selectedTemplateObj); }}
                disabled={selectedTemplate !== 'default' && tmplDownloading} data-testid="dl-statement-open">
                <Download className="w-4 h-4 mr-1.5" />Salary Statement
              </Button>
            )}
            {(canApprovePayroll || canEditPayroll) && (sheetLocked || groupRecords.length > 0) && (
              <Button variant="outline" className="border-purple-500 text-purple-700 hover:bg-purple-50" onClick={() => setReportsOpen(true)} data-testid="dl-reports-open">
                <Download className="w-4 h-4 mr-1.5" />Monthly Reports
              </Button>
            )}
            {canEditPayroll && (selectedTemplate !== 'default' || !sheetLocked) && groupRecords.length > 0 && (
              <Button variant="outline" className="border-red-500 text-red-700 hover:bg-red-50" onClick={handleBulkDelete} data-testid="bulk-delete-payroll-btn">
                <Trash2 className="w-4 h-4 mr-1.5" />Delete All
              </Button>
            )}
            {canGeneratePayroll && (
              <Button variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50" onClick={() => { setCoverageOpen(true); fetchCoverage(); }} data-testid="coverage-open-btn">
                <Users className="w-4 h-4 mr-1.5" />Who's Missing?
              </Button>
            )}
            {canEditPayroll && (
              <Button variant="outline" className="border-indigo-500 text-indigo-700 hover:bg-indigo-50" onClick={() => setApplyOpen(true)} data-testid="apply-master-open">
                <Check className="w-4 h-4 mr-1.5" />Apply to Master
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Template-wise salary sheet (Review Sheet view) */}
      {showTemplatePicker && selectedTemplate !== 'default' && viewMode === 'sheet' && (() => {
        const t = (templateView?.templates || []).find(x => x.template_id === selectedTemplate);
        if (templateViewLoading) return (
          <Card><CardContent className="py-16 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading template sheet…</CardContent></Card>
        );
        if (!t) return (
          <Card><CardContent className="py-16 text-center text-slate-500" data-testid="payroll-template-empty">No staff are covered by this template for {formatMonth(monthFilter)}.</CardContent></Card>
        );
        const hasUngen = (t.rows || []).some(r => r.generated === false);
        return (
          <Card data-testid={`payroll-template-sheet-${t.template_id}`}>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" />{t.name} — {formatMonth(monthFilter)}
                <span className="text-xs font-normal text-slate-500">({t.count} staff)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hasUngen && (
                <p className="mb-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="payroll-template-ungen-note">
                  Some staff have no generated payroll for {formatMonth(monthFilter)} yet — showing full-month figures. Generate payroll so attendance (days) is applied.
                </p>
              )}
              <div className="overflow-x-auto responsive-card-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-slate-600">
                      {t.columns.map(c => <th key={c.col_letter} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{c.header}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {t.rows.map(r => (
                      <tr key={r.employee_id} className={`border-t ${r.generated === false ? 'bg-amber-50/60' : ''}`} data-testid={`payroll-template-row-${r.employee_id}`}>
                        {t.columns.map((c, ci) => (
                          <td key={c.col_letter} className={`px-3 py-2 whitespace-nowrap ${c.kind === 'formula' ? 'text-indigo-700 font-medium' : 'text-slate-700'}`}>
                            {ci === 0 ? (
                              <span className="inline-flex items-center gap-2">
                                {tmoney(r.values[c.col_letter])}
                                {r.generated === false && <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0" data-testid={`payroll-template-ungen-badge-${r.employee_id}`}>Not generated</Badge>}
                              </span>
                            ) : tmoney(r.values[c.col_letter])}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="border-t-2 bg-slate-50 font-semibold text-slate-800" data-testid="payroll-template-total">
                      {t.columns.map((c, i) => (
                        <td key={c.col_letter} className="px-3 py-2 whitespace-nowrap">
                          {i === 0 ? 'TOTAL' : (t.totals[c.col_letter] !== undefined ? tmoney(t.totals[c.col_letter]) : '')}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Review Sheet view (full statement table for cross-checking before lock) */}
      {selectedTemplate === 'default' && !isEmployee && viewMode === 'sheet' && (
        <PayrollReviewSheet
          selectedEntity={selectedEntity}
          month={monthFilter}
          canLock={canApprovePayroll || canEditPayroll}
          onLockChange={setSheetLocked}
        />
      )}

      {/* Table */}
      {(isEmployee || viewMode === 'records') && (
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><IndianRupee className="w-4 h-4 text-[#DC2626]" />Payroll Records - {formatMonth(monthFilter)}</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-14 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : groupRecords.length === 0 ? (
            <div className="text-center py-12"><IndianRupee className="w-12 h-12 mx-auto text-slate-300 mb-3" /><h3 className="font-medium text-slate-900">No payroll records</h3><p className="text-sm text-slate-500">Generate payroll for this month</p></div>
          ) : (
            <div className="overflow-x-auto responsive-card-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead className="text-right">Basic</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Deductions</TableHead>
                    <TableHead className="text-right">Net Pay</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupRecords.map(pay => (
                    <TableRow key={pay.payroll_id} data-testid={`payroll-row-${pay.payroll_id}`}>
                      <TableCell><p className="font-medium text-sm">{getEmployeeName(pay.employee_id)}</p><code className="text-xs text-slate-400">{pay.employee_id}</code></TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(pay.basic_salary)}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-semibold">{formatCurrency(pay.gross_salary)}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-red-600">-{formatCurrency(pay.total_deductions)}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-bold text-emerald-600">{formatCurrency(pay.net_salary)}</TableCell>
                      <TableCell><Badge className={pay.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : pay.status === 'processed' ? 'bg-blue-100 text-blue-800' : pay.status === 'finalized' ? 'bg-purple-100 text-purple-800' : pay.status === 'paid' ? 'bg-green-100 text-green-800' : pay.status === 'corrections_needed' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-800'}>{pay.status === 'corrections_needed' ? 'Corrections Needed' : pay.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => setSelectedPayroll(pay)} data-testid={`view-payroll-${pay.payroll_id}`}><Eye className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="outline" className="text-blue-600" onClick={() => downloadSlip(pay)} data-testid={`download-slip-${pay.payroll_id}`}><Download className="w-3.5 h-3.5" /></Button>
                          {pay.status === 'processed' && !pay.verified && canApprovePayroll && (
                            <Button size="sm" variant="outline" className="text-blue-600" onClick={() => handleVerify(pay.payroll_id)} title="Verify (HR step 1)" data-testid={`verify-payroll-${pay.payroll_id}`}><Check className="w-3.5 h-3.5" /></Button>
                          )}
                          {pay.status === 'processed' && pay.verified && !pay.approved && canApprovePayroll && (
                            <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => handleApprove(pay.payroll_id)} title="Approve & release to employee (HR step 2)" data-testid={`approve-payroll-${pay.payroll_id}`}><CheckCircle className="w-3.5 h-3.5" /></Button>
                          )}
                          {(pay.status === 'approved' || pay.status === 'processed') && isFinance && (
                            <>
                              <Button size="sm" variant="outline" className="text-purple-600" onClick={() => handleFinanceVerify(pay.payroll_id)} title="Verify & Finalize" data-testid={`finance-verify-${pay.payroll_id}`}><CheckCircle className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="outline" className="text-amber-600" onClick={() => setShowRejectDialog(pay.payroll_id)} title="Send back for corrections" data-testid={`finance-reject-${pay.payroll_id}`}><RotateCcw className="w-3.5 h-3.5" /></Button>
                            </>
                          )}
                          {pay.status === 'finalized' && isFinance && (
                            <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleMarkPaid(pay.payroll_id)} title="Mark as Paid" data-testid={`mark-paid-${pay.payroll_id}`}><Banknote className="w-3.5 h-3.5" /></Button>
                          )}
                          {canEditPayroll && (
                            <Button size="sm" variant="ghost" className="text-indigo-600 hover:bg-indigo-50" title="Edit payroll attributes"
                              onClick={() => openEditPayroll(pay)}
                              data-testid={`edit-payroll-${pay.payroll_id}`}
                            ><Calculator className="w-3.5 h-3.5" /></Button>
                          )}
                          {canApprovePayroll && (
                            <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-600 hover:bg-red-50" title="Delete"
                              onClick={async () => {
                                if (!window.confirm('Delete this payroll record?')) return;
                                const res = await fetch(`${API_URL}/api/payroll/${pay.payroll_id}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' });
                                if (res.ok) { toast.success('Payroll record deleted'); fetchPayroll(); } else { toast.error('Delete failed'); }
                              }}
                              data-testid={`delete-payroll-${pay.payroll_id}`}
                            ><Trash2 className="w-3.5 h-3.5" /></Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Payslip Detail Dialog */}
      <Dialog open={!!selectedPayroll} onOpenChange={() => setSelectedPayroll(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Salary Slip - {selectedPayroll?.month && formatMonth(selectedPayroll.month)}</DialogTitle></DialogHeader>
          {selectedPayroll && (
            <div className="space-y-5 mt-3">
              <div className="text-center border-b pb-3">
                <h3 className="text-lg font-bold text-[#DC2626]">Sanjay Ghodawat Institute</h3>
                <p className="text-xs text-slate-500">{formatMonth(selectedPayroll.month)}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-slate-500 text-xs">Employee ID</p><p className="font-mono font-medium">{selectedPayroll.employee_id}</p></div>
                <div><p className="text-slate-500 text-xs">Name</p><p className="font-medium">{getEmployeeName(selectedPayroll.employee_id)}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-emerald-700 uppercase tracking-wider">Earnings</CardTitle></CardHeader>
                  <CardContent className="space-y-1.5 text-sm px-3 pb-3">
                    {((selectedPayroll.salary_type || 'breakdown') === 'consolidated' ? [
                      ['Pay (CTC)', selectedPayroll.consolidated_salary],
                      [`Payable / Month Days`, `${selectedPayroll.payable_days ?? selectedPayroll.present_days}/${selectedPayroll.month_days || selectedPayroll.working_days}`, true],
                      ['Basic (earned)', selectedPayroll.basic_salary],
                    ] : [
                      ['Pay', selectedPayroll.pay],
                      ['AGP', selectedPayroll.agp],
                      ['Gross Basic', selectedPayroll.gross_basic],
                      ['Basic (earned)', selectedPayroll.basic_salary],
                      ['DA', selectedPayroll.da],
                      ['HRA', selectedPayroll.hra],
                      ['CLA', selectedPayroll.cla],
                      ['Special Allowance', selectedPayroll.special_allowance],
                      ['Allowance', selectedPayroll.allowance],
                      ['Arrears', selectedPayroll.arrears],
                    ]).filter(r => r[2] || (r[1] !== undefined && r[1] !== 0)).map(([label, val, isText]) => (
                      <div key={label} className="flex justify-between"><span className="text-slate-600">{label}</span><span className="font-mono">{isText ? val : formatCurrency(val)}</span></div>
                    ))}
                    <div className="flex justify-between font-bold border-t pt-1.5 mt-1.5"><span>Gross</span><span className="font-mono">{formatCurrency(selectedPayroll.gross_salary)}</span></div>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-red-700 uppercase tracking-wider">Deductions</CardTitle></CardHeader>
                  <CardContent className="space-y-1.5 text-sm px-3 pb-3">
                    {[
                      ['PF', selectedPayroll.pf],
                      ['ESIC', selectedPayroll.esi],
                      ['Prof. Tax', selectedPayroll.professional_tax],
                      ['TDS', selectedPayroll.tds],
                      ['Bus Charges', selectedPayroll.bus_charges],
                      ['Advance', selectedPayroll.advance],
                      ['Staff Quarters', selectedPayroll.staff_quarters],
                      ['Electricity', selectedPayroll.electricity],
                      ['Local Mart', selectedPayroll.local_mart_deduction],
                      ['Late Penalty', selectedPayroll.late_penalty],
                      ['LOP', selectedPayroll.lop_amount],
                      ['Other', selectedPayroll.other_deductions],
                    ].filter(r => r[1] !== undefined && r[1] !== 0).map(([label, val]) => (
                      <div key={label} className="flex justify-between"><span className="text-slate-600">{label}</span><span className="font-mono">{formatCurrency(val)}</span></div>
                    ))}
                    <div className="flex justify-between font-bold border-t pt-1.5 mt-1.5"><span>Total</span><span className="font-mono">{formatCurrency(selectedPayroll.total_deductions)}</span></div>
                  </CardContent>
                </Card>
              </div>
              <Card className="bg-[#DC2626]/5 border-[#DC2626]/20">
                <CardContent className="p-3 flex justify-between items-center"><span className="font-semibold">Net Salary</span><span className="text-xl font-bold text-[#DC2626]">{formatCurrency(selectedPayroll.net_salary)}</span></CardContent>
              </Card>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSelectedPayroll(null)}>Close</Button>
                <Button className="btn-primary" onClick={() => downloadSlip(selectedPayroll)}><Download className="w-4 h-4 mr-1.5" />Download PDF</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Payroll Dialog - Super Admin / Finance only */}
      <Dialog open={!!editingPayroll} onOpenChange={(o) => !o && setEditingPayroll(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Payroll – {editingPayroll && getEmployeeName(editingPayroll.employee_id)}</DialogTitle>
            <p className="text-xs text-slate-500">{editingPayroll && formatMonth(editingPayroll.month)} · Admin / HR / Finance can edit values; system recomputes Gross & Net automatically.</p>
            {editingPayroll && (
              <div className="mt-2 p-2 rounded bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 text-[11px] text-emerald-900 dark:text-emerald-100">
                <strong>Basis:</strong> Payable Days {editingPayroll.payable_days ?? '—'} / Month Days {editingPayroll.month_days || editingPayroll.working_days || '—'}
                {editingPayroll.approved_leave_days !== undefined && (
                  <> · Present {editingPayroll.present_days ?? 0} + Approved Leaves {editingPayroll.approved_leave_days ?? 0}</>
                )}
                {' '}(Payable Days = month days − unapproved absences; week-offs, holidays &amp; paid leaves count as payable. Salary is prorated on <strong>payable days</strong>).
              </div>
            )}
          </DialogHeader>
          {editingPayroll && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setSavingEdit(true);
                try {
                  const numericFields = [
                    'consolidated_salary', 'pay', 'agp', 'gross_basic',
                    'basic_salary', 'da', 'hra', 'cla', 'ta',
                    'allowance', 'special_allowance', 'other_allowances', 'arrears',
                    'pf', 'esi', 'professional_tax', 'tds', 'late_penalty',
                    'bus_charges', 'advance', 'staff_quarters', 'electricity',
                    'local_mart_deduction', 'other_deductions',
                  ];
                  const payload = { salary_type: editingPayroll.salary_type || 'breakdown' };
                  for (const f of numericFields) {
                    if (editingPayroll[f] !== undefined && editingPayroll[f] !== '') payload[f] = Number(editingPayroll[f]) || 0;
                  }
                  if (editingPayroll.status) payload.status = editingPayroll.status;
                  if (editingPayroll._persist_to_master) payload.persist_to_master = true;
                  const res = await fetch(`${API_URL}/api/payroll/${editingPayroll.payroll_id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', ...getHeaders() },
                    body: JSON.stringify(payload),
                    credentials: 'include'
                  });
                  if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Update failed'); }
                  const updated = await res.json();
                  const persistedFields = Array.isArray(updated?._persisted_to_master) ? updated._persisted_to_master : [];
                  if (persistedFields.length > 0) {
                    toast.success(`Payroll updated + ${persistedFields.length} field(s) saved to employee master — will auto-apply next month`);
                  } else {
                    toast.success('Payroll updated');
                  }
                  setEditingPayroll(null);
                  fetchPayroll();
                } catch (err) { toast.error(err.message); }
                finally { setSavingEdit(false); }
              }}
              className="space-y-4 mt-3"
            >
              <div className="space-y-1.5">
                <Label>Salary Type</Label>
                <Select value={editingPayroll.salary_type || 'breakdown'} onValueChange={(v) => setEditingPayroll({ ...editingPayroll, salary_type: v })}>
                  <SelectTrigger data-testid="edit-payroll-salary-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="breakdown">Breakdown (Pay + AGP + Allowances)</SelectItem>
                    <SelectItem value="consolidated">Consolidated (flat monthly)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(editingPayroll.salary_type || 'breakdown') === 'consolidated' ? (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Consolidated Salary / Pay (₹)</Label>
                    <Input type="number" step="any" value={editingPayroll.consolidated_salary ?? 0}
                      onChange={(e) => setEditingPayroll({ ...editingPayroll, consolidated_salary: e.target.value })}
                      data-testid="edit-payroll-consolidated" />
                    <p className="text-xs text-slate-500">For consolidated staff: only Profession Tax + manual deductions (Advance, Local Mart) apply.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Basic (earned this month) ₹</Label>
                    <Input type="number" step="any" value={editingPayroll.basic_salary ?? 0}
                      onChange={(e) => setEditingPayroll({ ...editingPayroll, basic_salary: e.target.value })}
                      data-testid="edit-payroll-basic" />
                    <p className="text-xs text-slate-500">= Consolidated × (Payable Days / Working Days). Editable if needed.</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 border-t pt-3">
                    <p className="col-span-2 text-xs font-semibold uppercase text-slate-500">CTC structure (read-only on the slip, drives proration)</p>
                    {[
                      ['Pay', 'pay'],
                      ['AGP', 'agp'],
                    ].map(([label, key]) => (
                      <div key={key} className="space-y-1.5">
                        <Label>{label} (₹)</Label>
                        <Input type="number" step="any" value={editingPayroll[key] ?? 0}
                          onChange={(e) => setEditingPayroll({ ...editingPayroll, [key]: e.target.value })}
                          data-testid={`edit-payroll-${key}`} />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3 border-t pt-3">
                    <p className="col-span-2 text-xs font-semibold uppercase text-slate-500">Monthly Earnings</p>
                    {[
                      ['Basic (earned)', 'basic_salary'],
                      ['DA', 'da'],
                      ['HRA', 'hra'],
                      ['CLA', 'cla'],
                      ['Special Allowance', 'special_allowance'],
                      ['Allowance', 'allowance'],
                      ['Arrears', 'arrears'],
                    ].map(([label, key]) => (
                      <div key={key} className="space-y-1.5">
                        <Label>{label} (₹)</Label>
                        <Input type="number" step="any" value={editingPayroll[key] ?? 0}
                          onChange={(e) => setEditingPayroll({ ...editingPayroll, [key]: e.target.value })}
                          data-testid={`edit-payroll-${key}`} />
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="grid grid-cols-2 gap-3 border-t pt-3">
                <p className="col-span-2 text-xs font-semibold uppercase text-slate-500">Deductions</p>
                {(editingPayroll.salary_type === 'consolidated' ? [
                  ['Provident Fund', 'pf'],
                  ['Professional Tax', 'professional_tax'],
                  ['ESIC', 'esi'],
                  ['TDS', 'tds'],
                  ['Bus Charges', 'bus_charges'],
                  ['Advance', 'advance'],
                  ['Staff Quarters', 'staff_quarters'],
                  ['Electricity', 'electricity'],
                  ['Local Mart Deduction', 'local_mart_deduction'],
                  ['Other Deductions', 'other_deductions'],
                ] : [
                  ['Provident Fund', 'pf'],
                  ['ESIC', 'esi'],
                  ['Professional Tax', 'professional_tax'],
                  ['TDS', 'tds'],
                  ['Bus Charges', 'bus_charges'],
                  ['Advance', 'advance'],
                  ['Staff Quarters', 'staff_quarters'],
                  ['Electricity', 'electricity'],
                  ['Local Mart Deduction', 'local_mart_deduction'],
                  ['Late Penalty', 'late_penalty'],
                  ['Other Deductions', 'other_deductions'],
                ]).map(([label, key]) => (
                  <div key={key} className="space-y-1.5">
                    <Label>{label} (₹)</Label>
                    <Input type="number" step="any" value={editingPayroll[key] ?? 0}
                      onChange={(e) => setEditingPayroll({ ...editingPayroll, [key]: e.target.value })}
                      data-testid={`edit-payroll-${key}`} />
                  </div>
                ))}
              </div>

              <div className="space-y-1.5 border-t pt-3">
                <Label>Status</Label>
                <Select value={editingPayroll.status || 'processed'} onValueChange={(v) => setEditingPayroll({ ...editingPayroll, status: v })}>
                  <SelectTrigger data-testid="edit-payroll-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="processed">Processed</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="finalized">Finalized</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="corrections_needed">Corrections Needed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-3 bg-blue-50 dark:bg-blue-950/30 -mx-4 px-4 py-3 rounded">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editingPayroll._persist_to_master}
                    onChange={(e) => setEditingPayroll({ ...editingPayroll, _persist_to_master: e.target.checked })}
                    className="mt-1"
                    data-testid="persist-to-master-toggle"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Also save to Employee Master (auto-apply every month)</p>
                    <p className="text-[11px] text-blue-800/80 dark:text-blue-200/80 mt-0.5">
                      When ticked, the CTC-structure earnings + permanent deductions (PF, PT, Bus, Staff Quarters, Local Mart, Other Deduction) you edit here will also update the employee&apos;s master record — so next month&apos;s payroll uses these values automatically without re-entry.
                    </p>
                  </div>
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditingPayroll(null)}>Cancel</Button>
                <Button type="submit" className="btn-primary" disabled={savingEdit} data-testid="save-payroll-edit-btn">
                  {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle className="w-4 h-4 mr-1.5" />}
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Template-wise Edit Dialog (for staff covered by an Excel salary template) */}
      <Dialog open={!!templateEdit} onOpenChange={(o) => !o && setTemplateEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Salary — {templateEdit?.employee?.name}</DialogTitle>
            <p className="text-xs text-slate-500">
              {templateEdit && formatMonth(templateEdit.month)} · Template: <strong>{templateEdit?.template?.name}</strong>. Edit the input figures below; formula columns recalculate automatically.
            </p>
          </DialogHeader>
          {templateEdit && (
            <div className="space-y-4 mt-2">
              <div className="overflow-x-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Column</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templateEdit.columns.map((c) => (
                      <TableRow key={c.col_letter} data-testid={`tmpl-edit-row-${c.col_letter}`}>
                        <TableCell className="py-2">
                          <span className="font-medium text-sm">{c.header}</span>
                          {c.kind !== 'field' && (
                            <Badge variant="outline" className="ml-2 text-[10px] uppercase">{c.kind === 'formula' ? 'auto' : 'fixed'}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right py-2">
                          {c.editable ? (
                            <Input type="number" step="any" className="w-32 ml-auto text-right h-8"
                              value={templateEditInputs[c.field_key] ?? 0}
                              onChange={(e) => setTemplateEditInputs({ ...templateEditInputs, [c.field_key]: e.target.value })}
                              data-testid={`tmpl-edit-input-${c.field_key}`} />
                          ) : (
                            <span className="font-mono text-sm text-slate-700" data-testid={`tmpl-edit-value-${c.col_letter}`}>
                              {typeof c.value === 'number' ? formatCurrency(c.value) : (c.value ?? '—')}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/30 rounded px-3 py-2.5">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" className="mt-1" checked={templatePersist}
                    onChange={(e) => setTemplatePersist(e.target.checked)}
                    data-testid="tmpl-persist-master" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Also save these figures to Employee Master</p>
                    <p className="text-[11px] text-blue-800/80 dark:text-blue-200/80 mt-0.5">Updates the employee&apos;s master values so this template picks them up automatically next month.</p>
                  </div>
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setTemplateEdit(null)}>Cancel</Button>
                <Button type="button" variant="outline" onClick={() => submitTemplateEdit(false)} disabled={savingTemplateEdit} data-testid="tmpl-recalc-btn">
                  {savingTemplateEdit ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                  Recalculate
                </Button>
                <Button type="button" className="btn-primary" onClick={() => submitTemplateEdit(true)} disabled={savingTemplateEdit} data-testid="tmpl-save-btn">
                  {savingTemplateEdit ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle className="w-4 h-4 mr-1.5" />}
                  Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Bulk Apply-to-Master Dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Apply {formatMonth(monthFilter)} Values to Employee Master</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-3">
            <p className="text-xs text-slate-600">
              Copies this month&apos;s payroll values (Basic, AGP, DA, HRA, CLA, allowances &amp; all deductions)
              into the Employee Master for the selected staff — so they automatically apply from next month.
              Prorated earnings are converted back to full-month values.
            </p>
            {(() => {
              const list = payrollRecords;
              const selCount = list.filter(p => applyChecked[p.employee_id]).length;
              const allSel = list.length > 0 && selCount === list.length;
              return (
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-2">
                    <input type="checkbox" checked={allSel}
                      onChange={(e) => {
                        const m = { ...applyChecked };
                        list.forEach(p => { m[p.employee_id] = e.target.checked; });
                        setApplyChecked(m);
                      }}
                      data-testid="apply-select-all" />
                    Select All ({selCount}/{list.length})
                  </Label>
                  <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1" data-testid="apply-employee-list">
                    {list.map(p => (
                      <label key={p.payroll_id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer hover:bg-slate-50 rounded px-1">
                        <input type="checkbox" checked={!!applyChecked[p.employee_id]}
                          onChange={(e) => setApplyChecked({ ...applyChecked, [p.employee_id]: e.target.checked })}
                          data-testid={`apply-emp-${p.employee_id}`} />
                        <span className="flex-1">{p.employee_name || p.employee_id}</span>
                        <span className="text-xs text-slate-400">{formatCurrency(p.net_salary)}</span>
                      </label>
                    ))}
                    {list.length === 0 && <p className="text-xs text-slate-500 text-center py-2">No payroll records for this month</p>}
                  </div>
                </div>
              );
            })()}
            <div className="flex justify-end gap-3 pt-1">
              <Button variant="outline" onClick={() => setApplyOpen(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleBulkApplyToMaster} disabled={applying} data-testid="apply-master-confirm">
                {applying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}Apply to Master
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Salary Statement Download Dialog — category + employee selection */}
      <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Download Salary Statement — {formatMonth(monthFilter)}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-3">
            <p className="text-xs text-slate-500 -mt-1">Filters are drawn live from master data. Pick one or many in each — the sheet is built in the exact employee sequence set in master data.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Staff Category</Label>
                <MultiSelect
                  options={categoryOptions}
                  selected={stmtCategories}
                  onChange={setStmtCategories}
                  allLabel="All Categories"
                  emptyText="No categories"
                  testId="stmt-category-select"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Department</Label>
                <MultiSelect
                  options={departmentOptions}
                  selected={stmtDepts}
                  onChange={setStmtDepts}
                  allLabel="All Departments"
                  emptyText="No departments"
                  testId="stmt-department-select"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Appointment Type</Label>
                <MultiSelect
                  options={appointmentTypeOptions}
                  selected={stmtApptTypes}
                  onChange={setStmtApptTypes}
                  allLabel="All Types"
                  emptyText="No types"
                  testId="stmt-appointment-select"
                />
              </div>
            </div>
            {(() => {
              const list = filterStatementList(stmtCategories, stmtDepts, stmtApptTypes);
              const selCount = list.filter(e => stmtChecked[e.employee_id]).length;
              const allSel = list.length > 0 && selCount === list.length;
              return (
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-2">
                    <input type="checkbox" checked={allSel}
                      onChange={(e) => {
                        const m = { ...stmtChecked };
                        list.forEach(emp => { m[emp.employee_id] = e.target.checked; });
                        setStmtChecked(m);
                      }}
                      data-testid="stmt-select-all" />
                    Select All ({selCount}/{list.length})
                  </Label>
                  <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1" data-testid="stmt-employee-list">
                    {list.map(emp => (
                      <label key={emp.employee_id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer hover:bg-slate-50 rounded px-1">
                        <input type="checkbox" checked={!!stmtChecked[emp.employee_id]}
                          onChange={(e) => setStmtChecked({ ...stmtChecked, [emp.employee_id]: e.target.checked })}
                          data-testid={`stmt-emp-${emp.employee_id}`} />
                        <span className="flex-1">{fullName(emp)}</span>
                        <span className="text-xs text-slate-400">{emp.department || '—'} · {emp.category || '—'}</span>
                      </label>
                    ))}
                    {list.length === 0 && <p className="text-xs text-slate-500 text-center py-2">No employees match these filters</p>}
                  </div>
                </div>
              );
            })()}
            <div className="flex justify-end gap-3 pt-1">
              <Button variant="outline" onClick={() => setStmtOpen(false)}>Cancel</Button>
              <Button className="btn-primary" data-testid="stmt-download-btn"
                onClick={async () => {
                  const list = filterStatementList(stmtCategories, stmtDepts, stmtApptTypes);
                  const ids = list.filter(e => stmtChecked[e.employee_id]).map(e => e.employee_id);
                  if (ids.length === 0) { toast.error('Select at least one employee'); return; }
                  const allCats = stmtCategories.length === categoryOptions.length;
                  const title = (allCats || stmtCategories.length === 0)
                    ? 'ALL STAFF'
                    : stmtCategories.join(', ').toUpperCase();
                  await handleSalaryStatementDownload(ids, title);
                  setStmtOpen(false);
                }}>
                <Download className="w-4 h-4 mr-1.5" />Download
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Monthly Statutory Reports Dialog */}
      <Dialog open={reportsOpen} onOpenChange={setReportsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Monthly Salary Reports — {formatMonth(monthFilter)}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-slate-500">Figures are taken from the generated payroll for this month. Statements print on A3, 12 employees per page, with signatory footer.</p>
            <Button className="w-full justify-start btn-primary" data-testid="dl-full-pack-btn"
              onClick={() => { downloadReportFile('workbook', { include_descendants: 'true' }, 'salary_reports'); setReportsOpen(false); }}>
              <Download className="w-4 h-4 mr-2" />Full Monthly Pack (all sheets)
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="justify-start" data-testid="dl-summary-btn"
                onClick={() => downloadReportFile('summary', { include_descendants: 'true' }, 'salary_summary')}>
                <Download className="w-4 h-4 mr-2" />Summary
              </Button>
              <Button variant="outline" className="justify-start" data-testid="dl-netpay-btn"
                onClick={() => downloadReportFile('net-pay', {}, 'net_pay')}>
                <Download className="w-4 h-4 mr-2" />Net Pay / Bank
              </Button>
              <Button variant="outline" className="justify-start" data-testid="dl-pf-btn"
                onClick={() => downloadReportFile('pf', {}, 'pf_report')}>
                <Download className="w-4 h-4 mr-2" />PF Listing
              </Button>
              <Button variant="outline" className="justify-start" data-testid="dl-pt-btn"
                onClick={() => downloadReportFile('pt', {}, 'pt_report')}>
                <Download className="w-4 h-4 mr-2" />PT Listing
              </Button>
            </div>
            <div className="flex justify-end pt-1">
              <Button variant="outline" onClick={() => setReportsOpen(false)}>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payroll Coverage / Who's Missing Diagnostic */}
      <Dialog open={coverageOpen} onOpenChange={setCoverageOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Payroll Coverage — {selectedEntity?.name || 'Institute'} · {formatMonth(monthFilter)}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-1">
            <p className="text-xs text-slate-500">Shows everyone this month's payroll run considers (including sub-wings and staff who left mid-month) and why each will or won't generate.</p>
            <div className="flex items-center gap-2">
              <Input placeholder="Find a name anywhere in the org (e.g. Komal)…" value={coverageQ}
                onChange={e => setCoverageQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') fetchCoverage(); }}
                data-testid="coverage-search-input" className="h-9" />
              <Button onClick={fetchCoverage} disabled={coverageLoading} data-testid="coverage-search-btn">
                {coverageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check'}
              </Button>
            </div>
            {coverage && (
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge className="bg-emerald-100 text-emerald-700">Will generate: {coverage.summary?.will_generate || 0}</Badge>
                <Badge className="bg-slate-100 text-slate-700">Already in payroll: {coverage.summary?.already_in_payroll || 0}</Badge>
                <Badge className="bg-amber-100 text-amber-700">No attendance: {coverage.summary?.no_attendance || 0}</Badge>
                <Badge className="bg-rose-100 text-rose-700">Zero salary: {coverage.summary?.zero_salary || 0}</Badge>
              </div>
            )}
            {coverage?.name_search?.length > 0 && (
              <div className="border rounded-md p-2 bg-amber-50" data-testid="coverage-name-results">
                <p className="text-xs font-semibold text-amber-800 mb-1">Name search results (across all institutes):</p>
                {coverage.name_search.map(h => (
                  <div key={h.employee_id} className="text-xs text-slate-700 py-0.5">
                    <strong>{h.name}</strong> — wing: <strong>{h.entity_name}</strong> · {h.is_active ? 'Active' : 'Left/Inactive'}
                    {h.leaving_date ? ` (left ${h.leaving_date})` : ''} · {h.in_selected_scope ? 'in this selection ✅' : 'NOT in your selected institute ❌'}
                  </div>
                ))}
              </div>
            )}
            <div className="max-h-80 overflow-auto border rounded-md" data-testid="coverage-table">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left"><th className="p-2">Name</th><th className="p-2">Wing</th><th className="p-2">Status</th><th className="p-2">Served</th><th className="p-2">Gross</th><th className="p-2">Result</th></tr>
                </thead>
                <tbody>
                  {(coverage?.employees || []).map(r => (
                    <tr key={r.employee_id} className="border-t" data-testid={`coverage-row-${r.employee_id}`}>
                      <td className="p-2 font-medium">{r.name}</td>
                      <td className="p-2">{r.entity_name}</td>
                      <td className="p-2">{r.is_active ? 'Active' : 'Left'}{r.leaving_date ? ` (${r.leaving_date})` : ''}</td>
                      <td className="p-2">{r.served_days ?? '—'}</td>
                      <td className="p-2">{r.gross != null ? r.gross.toLocaleString() : '—'}</td>
                      <td className={`p-2 ${r.reason?.startsWith('ready') ? 'text-emerald-700' : r.reason?.startsWith('already') ? 'text-slate-500' : 'text-rose-600'}`}>{r.reason}</td>
                    </tr>
                  ))}
                  {coverage && (coverage.employees || []).length === 0 && (
                    <tr><td colSpan={6} className="p-3 text-center text-slate-500">No staff found for this institute + month.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end"><Button variant="outline" onClick={() => setCoverageOpen(false)}>Close</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showRejectDialog} onOpenChange={() => setShowRejectDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Back for Corrections</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-3">
            <p className="text-sm text-slate-600">Add a comment explaining what corrections are needed:</p>
            <Input placeholder="Correction details..." value={rejectComment} onChange={e => setRejectComment(e.target.value)} data-testid="reject-comment" />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowRejectDialog(null)}>Cancel</Button>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => handleFinanceReject(showRejectDialog)} data-testid="confirm-reject-btn">
                <RotateCcw className="w-4 h-4 mr-1.5" />Send Back to HR
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== SALARY HISTORY TAB ==========
function SalaryHistoryTab({ myEmployeeId, employees, isEmployee }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmpId, setSelectedEmpId] = useState(myEmployeeId || '');

  const fetchHistory = useCallback(async (empId) => {
    if (!empId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payroll/history/${empId}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) setHistory(await res.json());
      else toast.error('Failed to load salary history');
    } catch { toast.error('Failed to load salary history'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (myEmployeeId) { setSelectedEmpId(myEmployeeId); fetchHistory(myEmployeeId); }
    else if (employees.length > 0 && !isEmployee) { setSelectedEmpId(employees[0].employee_id); fetchHistory(employees[0].employee_id); }
  }, [myEmployeeId, employees, isEmployee, fetchHistory]);

  const chartData = history?.history?.slice().reverse().map(p => ({
    month: formatMonth(p.month),
    gross: p.gross_salary,
    net: p.net_salary,
    deductions: p.total_deductions
  })) || [];

  return (
    <div className="space-y-5">
      {!isEmployee && (
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Label className="whitespace-nowrap">Employee</Label>
            <Select value={selectedEmpId} onValueChange={v => { setSelectedEmpId(v); fetchHistory(v); }}>
              <SelectTrigger className="max-w-xs" data-testid="history-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{fullName(e)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent></Card>
      )}

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : !history || history.history?.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" /><h3 className="font-medium">No salary history</h3><p className="text-sm text-slate-500">No payroll records found</p></CardContent></Card>
      ) : (
        <>
          {/* Yearly Summary Cards */}
          {history.yearly_totals && Object.keys(history.yearly_totals).length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(history.yearly_totals).sort(([a],[b]) => b.localeCompare(a)).slice(0, 3).map(([year, totals]) => (
                <Card key={year} data-testid={`yearly-summary-${year}`}>
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-500">FY {year}</CardTitle></CardHeader>
                  <CardContent className="space-y-1.5">
                    <div className="flex justify-between text-sm"><span className="text-slate-600">Gross</span><span className="font-mono font-semibold">{formatCurrency(totals.gross)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-600">Net</span><span className="font-mono font-semibold text-emerald-600">{formatCurrency(totals.net)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-600">PF</span><span className="font-mono">{formatCurrency(totals.pf)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-slate-600">TDS</span><span className="font-mono">{formatCurrency(totals.tds)}</span></div>
                    <p className="text-xs text-slate-400 pt-1">{totals.months} month(s)</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Chart */}
          {chartData.length > 1 && (
            <Card data-testid="salary-chart">
              <CardHeader><CardTitle className="text-base">Salary Trend</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Legend />
                    <Line type="monotone" dataKey="gross" name="Gross" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="net" name="Net" stroke="#16A34A" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="deductions" name="Deductions" stroke="#DC2626" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* History Table */}
          <Card>
            <CardHeader><CardTitle className="text-base">Monthly Records</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto responsive-card-table">
                <Table>
                  <TableHeader><TableRow><TableHead>Month</TableHead><TableHead className="text-right">Basic</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">PF</TableHead><TableHead className="text-right">TDS</TableHead><TableHead className="text-right">Net</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {history.history.map(p => (
                      <TableRow key={p.payroll_id} data-testid={`history-row-${p.payroll_id}`}>
                        <TableCell className="font-medium">{formatMonth(p.month)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(p.basic_salary)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(p.gross_salary)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(p.pf)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(p.tds)}</TableCell>
                        <TableCell className="text-right font-mono text-sm font-bold text-emerald-600">{formatCurrency(p.net_salary)}</TableCell>
                        <TableCell><Badge className={p.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}>{p.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ========== FORM 16 TAB ==========
function Form16Tab({ myEmployeeId, employees, isEmployee }) {
  const [selectedEmpId, setSelectedEmpId] = useState(myEmployeeId || '');
  const [financialYear, setFinancialYear] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { if (myEmployeeId) setSelectedEmpId(myEmployeeId); }, [myEmployeeId]);

  // Generate FY options (current year and 2 previous)
  const now = new Date();
  const currentFY = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const fyOptions = Array.from({length: 3}, (_, i) => {
    const y = currentFY - i;
    return { value: `${y}-${(y+1).toString().slice(2)}`, label: `FY ${y}-${(y+1).toString().slice(2)}` };
  });

  useEffect(() => { if (!financialYear && fyOptions.length) setFinancialYear(fyOptions[0].value); }, [fyOptions, financialYear]);

  const handleDownload = async () => {
    if (!selectedEmpId || !financialYear) { toast.error('Select employee and financial year'); return; }
    setDownloading(true);
    try {
      const res = await fetch(`${API_URL}/api/payroll/form16/${selectedEmpId}/${financialYear}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `Form16_${financialYear}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
        toast.success('Form 16 downloaded');
      } else { const err = await res.json(); toast.error(err.detail || 'Failed to download Form 16'); }
    } catch { toast.error('Failed to download Form 16'); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-5">
      <Card data-testid="form16-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#DC2626]" />Form 16 - Tax Certificate</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-slate-600">Download your Form 16 (Certificate under section 203 of the Income-tax Act, 1961) for tax filing purposes.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {!isEmployee && (
              <div className="space-y-1.5">
                <Label>Employee</Label>
                <Select value={selectedEmpId} onValueChange={setSelectedEmpId}>
                  <SelectTrigger data-testid="form16-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{fullName(e)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Financial Year</Label>
              <Select value={financialYear} onValueChange={setFinancialYear}>
                <SelectTrigger data-testid="form16-fy-select"><SelectValue placeholder="Select FY" /></SelectTrigger>
                <SelectContent>{fyOptions.map(fy => <SelectItem key={fy.value} value={fy.value}>{fy.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button className="btn-primary w-full" onClick={handleDownload} disabled={downloading} data-testid="download-form16-btn">
                {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Download className="w-4 h-4 mr-1.5" />}
                Download Form 16
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="p-4 text-center">
                <FileText className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm font-medium">Part A</p>
                <p className="text-xs text-slate-500">Details of Tax Deducted & Deposited</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="p-4 text-center">
                <Calculator className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm font-medium">Part B</p>
                <p className="text-xs text-slate-500">Details of Salary & Tax Computation</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="p-4 text-center">
                <Shield className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm font-medium">Annexure</p>
                <p className="text-xs text-slate-500">Deductions under Chapter VI-A</p>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ========== PF/ESI STATEMENT TAB ==========
function PFESITab({ myEmployeeId, employees, isEmployee }) {
  const [selectedEmpId, setSelectedEmpId] = useState(myEmployeeId || '');
  const [financialYear, setFinancialYear] = useState('');
  const [pfData, setPfData] = useState(null);
  const [esiData, setEsiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeStatement, setActiveStatement] = useState('pf');

  useEffect(() => { if (myEmployeeId) setSelectedEmpId(myEmployeeId); }, [myEmployeeId]);

  const now = new Date();
  const currentFY = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const fyOptions = Array.from({length: 3}, (_, i) => {
    const y = currentFY - i;
    return { value: `${y}-${(y+1).toString().slice(2)}`, label: `FY ${y}-${(y+1).toString().slice(2)}` };
  });

  useEffect(() => { if (!financialYear && fyOptions.length) setFinancialYear(fyOptions[0].value); }, [fyOptions, financialYear]);

  const fetchStatements = useCallback(async () => {
    if (!selectedEmpId) return;
    setLoading(true);
    try {
      const fyParam = financialYear ? `?financial_year=${financialYear}` : '';
      const [pfRes, esiRes] = await Promise.all([
        fetch(`${API_URL}/api/payroll/pf-statement/${selectedEmpId}${fyParam}`, { headers: getHeaders(), credentials: 'include' }),
        fetch(`${API_URL}/api/payroll/esi-statement/${selectedEmpId}${fyParam}`, { headers: getHeaders(), credentials: 'include' })
      ]);
      if (pfRes.ok) setPfData(await pfRes.json());
      if (esiRes.ok) setEsiData(await esiRes.json());
    } catch { toast.error('Failed to load statements'); }
    finally { setLoading(false); }
  }, [selectedEmpId, financialYear]);

  useEffect(() => { if (selectedEmpId) fetchStatements(); }, [selectedEmpId, financialYear, fetchStatements]);

  return (
    <div className="space-y-5">
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            {!isEmployee && (
              <div className="flex-1 space-y-1.5">
                <Label>Employee</Label>
                <Select value={selectedEmpId} onValueChange={setSelectedEmpId}>
                  <SelectTrigger data-testid="pfesi-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{fullName(e)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Financial Year</Label>
              <Select value={financialYear} onValueChange={setFinancialYear}>
                <SelectTrigger className="w-[160px]" data-testid="pfesi-fy-select"><SelectValue /></SelectTrigger>
                <SelectContent>{fyOptions.map(fy => <SelectItem key={fy.value} value={fy.value}>{fy.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PF/ESI Toggle */}
      <div className="flex gap-2">
        <Button variant={activeStatement === 'pf' ? 'default' : 'outline'} onClick={() => setActiveStatement('pf')} data-testid="pf-tab-btn">
          <Shield className="w-4 h-4 mr-1.5" />Provident Fund (PF)
        </Button>
        <Button variant={activeStatement === 'esi' ? 'default' : 'outline'} onClick={() => setActiveStatement('esi')} data-testid="esi-tab-btn">
          <Shield className="w-4 h-4 mr-1.5" />ESI
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : activeStatement === 'pf' ? (
        /* PF Statement */
        pfData ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Card data-testid="pf-summary-employee"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Employee PF</p><p className="text-xl font-bold mt-1">{formatCurrency(pfData.summary?.total_employee_contribution)}</p></CardContent></Card>
              <Card data-testid="pf-summary-employer"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Employer PF</p><p className="text-xl font-bold mt-1">{formatCurrency(pfData.summary?.total_employer_contribution)}</p></CardContent></Card>
              <Card data-testid="pf-summary-total"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Total PF</p><p className="text-xl font-bold mt-1 text-emerald-600">{formatCurrency(pfData.summary?.total_pf)}</p></CardContent></Card>
              <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Interest Rate</p><p className="text-xl font-bold mt-1">{pfData.summary?.interest_rate}</p><p className="text-xs text-slate-400">{pfData.summary?.months_contributed} months</p></CardContent></Card>
            </div>
            <Card>
              <CardHeader><CardTitle className="text-base">Monthly PF Contributions</CardTitle></CardHeader>
              <CardContent>
                {pfData.records?.length === 0 ? (
                  <p className="text-center py-8 text-slate-500">No PF records found for this period</p>
                ) : (
                  <div className="overflow-x-auto responsive-card-table">
                    <Table>
                      <TableHeader><TableRow><TableHead>Month</TableHead><TableHead className="text-right">Basic</TableHead><TableHead className="text-right">Employee (12%)</TableHead><TableHead className="text-right">Employer (12%)</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {pfData.records?.map((r, i) => (
                          <TableRow key={i}><TableCell>{formatMonth(r.month)}</TableCell><TableCell className="text-right font-mono text-sm">{formatCurrency(r.basic_salary)}</TableCell><TableCell className="text-right font-mono text-sm">{formatCurrency(r.employee_contribution)}</TableCell><TableCell className="text-right font-mono text-sm">{formatCurrency(r.employer_contribution)}</TableCell><TableCell className="text-right font-mono text-sm font-semibold">{formatCurrency(r.total)}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : <Card><CardContent className="py-12 text-center"><Shield className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">Select an employee to view PF statement</p></CardContent></Card>
      ) : (
        /* ESI Statement */
        esiData ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Card data-testid="esi-summary-employee"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Employee ESI (0.75%)</p><p className="text-xl font-bold mt-1">{formatCurrency(esiData.summary?.total_employee_contribution)}</p></CardContent></Card>
              <Card data-testid="esi-summary-employer"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Employer ESI (3.25%)</p><p className="text-xl font-bold mt-1">{formatCurrency(esiData.summary?.total_employer_contribution)}</p></CardContent></Card>
              <Card data-testid="esi-summary-total"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Total ESI</p><p className="text-xl font-bold mt-1 text-emerald-600">{formatCurrency(esiData.summary?.total_esi)}</p></CardContent></Card>
            </div>
            <Card>
              <CardHeader><CardTitle className="text-base">Monthly ESI Contributions</CardTitle></CardHeader>
              <CardContent>
                {esiData.records?.length === 0 ? (
                  <p className="text-center py-8 text-slate-500">No ESI records found for this period</p>
                ) : (
                  <div className="overflow-x-auto responsive-card-table">
                    <Table>
                      <TableHeader><TableRow><TableHead>Month</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Employee (0.75%)</TableHead><TableHead className="text-right">Employer (3.25%)</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Applicable</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {esiData.records?.map((r, i) => (
                          <TableRow key={i}><TableCell>{formatMonth(r.month)}</TableCell><TableCell className="text-right font-mono text-sm">{formatCurrency(r.gross_salary)}</TableCell><TableCell className="text-right font-mono text-sm">{formatCurrency(r.employee_contribution)}</TableCell><TableCell className="text-right font-mono text-sm">{formatCurrency(r.employer_contribution)}</TableCell><TableCell className="text-right font-mono text-sm font-semibold">{formatCurrency(r.total)}</TableCell><TableCell><Badge className={r.applicable ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}>{r.applicable ? 'Yes' : 'No'}</Badge></TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : <Card><CardContent className="py-12 text-center"><Shield className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">Select an employee to view ESI statement</p></CardContent></Card>
      )}
    </div>
  );
}

// ========== PAYROLL DASHBOARD TAB ==========
function PayrollDashboardTab({ selectedEntity, entities }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/payroll/dashboard`, { headers: getHeaders(), credentials: 'include' });
        if (res.ok) setDashboard(await res.json());
        else toast.error('Failed to load dashboard');
      } catch { toast.error('Failed to load dashboard'); }
      finally { setLoading(false); }
    };
    fetchDashboard();
  }, []);

  const downloadReport = async (type) => {
    try {
      const params = new URLSearchParams({ report_type: type });
      if (selectedEntity) params.append('entity_id', selectedEntity.entity_id);
      if (dashboard?.current_month) params.append('month', dashboard.current_month);
      const res = await fetch(`${API_URL}/api/payroll/reports/download?${params}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `payroll_report_${type}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
        toast.success('Report downloaded');
      } else toast.error('Failed to download report');
    } catch { toast.error('Failed to download report'); }
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-slate-100 rounded animate-pulse" />)}</div>;
  if (!dashboard) return <Card><CardContent className="py-12 text-center"><BarChart3 className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">No dashboard data</p></CardContent></Card>;

  const monthChartData = dashboard.month_breakdown?.slice().reverse().map(m => ({
    month: formatMonth(m.month),
    gross: m.total_gross,
    net: m.total_net,
    pf: m.total_pf,
    tds: m.total_tds,
    records: m.total_records
  })) || [];

  const institutePieData = dashboard.institute_summary?.map((inst, i) => ({
    name: inst.entity_name?.length > 15 ? inst.entity_code || inst.entity_name.slice(0, 15) : inst.entity_name,
    value: inst.total_gross,
    fill: CHART_COLORS[i % CHART_COLORS.length]
  })).filter(d => d.value > 0) || [];

  return (
    <div className="space-y-5">
      {/* Top Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="dash-total-employees"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Total Employees</p><p className="text-2xl font-bold mt-1">{dashboard.total_employees}</p></CardContent></Card>
        <Card data-testid="dash-processed"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Processed</p><p className="text-2xl font-bold mt-1 text-blue-600">{dashboard.payroll_processed}</p><p className="text-xs text-slate-400">{dashboard.pending_payroll} pending</p></CardContent></Card>
        <Card data-testid="dash-total-gross"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Total Gross</p><p className="text-xl font-bold mt-1">{formatCurrency(dashboard.total_gross)}</p></CardContent></Card>
        <Card data-testid="dash-total-net"><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase tracking-wider">Net Payable</p><p className="text-xl font-bold mt-1 text-emerald-600">{formatCurrency(dashboard.total_net)}</p></CardContent></Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Monthly Trend */}
        <Card className="lg:col-span-2" data-testid="monthly-trend-chart">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base">Monthly Payroll Trend (Last 6 Months)</CardTitle>
            <Button size="sm" variant="outline" onClick={() => downloadReport('summary')} data-testid="download-summary-report"><Download className="w-3.5 h-3.5 mr-1" />Report</Button>
          </CardHeader>
          <CardContent>
            {monthChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/100000).toFixed(1)}L`} />
                  <Tooltip formatter={v => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="gross" name="Gross" fill="#2563EB" radius={[4,4,0,0]} />
                  <Bar dataKey="net" name="Net" fill="#16A34A" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center py-8 text-slate-500">No trend data available</p>}
          </CardContent>
        </Card>

        {/* Institute Distribution */}
        <Card data-testid="institute-pie-chart">
          <CardHeader><CardTitle className="text-base">Institute Distribution</CardTitle></CardHeader>
          <CardContent>
            {institutePieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={institutePieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" label={({name}) => name}>
                    {institutePieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip formatter={v => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-center py-8 text-slate-500">No institute data</p>}
          </CardContent>
        </Card>
      </div>

      {/* Institute-wise Table */}
      {dashboard.institute_summary?.length > 0 && (
        <Card data-testid="institute-summary-table">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4" />Institute-wise Summary ({formatMonth(dashboard.current_month)})</CardTitle>
            <Button size="sm" variant="outline" onClick={() => downloadReport('full')} data-testid="download-full-report"><Download className="w-3.5 h-3.5 mr-1" />Full Report</Button>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto responsive-card-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Institute</TableHead>
                    <TableHead className="text-right">Employees</TableHead>
                    <TableHead className="text-right">Processed</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Deductions</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboard.institute_summary.map(inst => (
                    <TableRow key={inst.entity_id} data-testid={`institute-row-${inst.entity_id}`}>
                      <TableCell><p className="font-medium text-sm">{inst.entity_name}</p></TableCell>
                      <TableCell className="text-right">{inst.total_employees}</TableCell>
                      <TableCell className="text-right">{inst.payroll_processed}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(inst.total_gross)}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-red-600">{formatCurrency(inst.total_deductions)}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-bold text-emerald-600">{formatCurrency(inst.total_net)}</TableCell>
                      <TableCell><Badge className={inst.status === 'complete' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>{inst.pending > 0 ? `${inst.pending} pending` : 'Complete'}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deductions Breakdown */}
      <Card data-testid="deductions-breakdown">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base">Deductions Overview</CardTitle>
          <Button size="sm" variant="outline" onClick={() => downloadReport('deductions')}><Download className="w-3.5 h-3.5 mr-1" />Deductions Report</Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {dashboard.month_breakdown?.[0] ? (
              <>
                <div className="text-center p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500 uppercase">PF</p><p className="text-lg font-bold mt-1">{formatCurrency(dashboard.month_breakdown[0].total_pf)}</p></div>
                <div className="text-center p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500 uppercase">TDS</p><p className="text-lg font-bold mt-1">{formatCurrency(dashboard.month_breakdown[0].total_tds)}</p></div>
                <div className="text-center p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500 uppercase">Approved</p><p className="text-lg font-bold mt-1 text-emerald-600">{dashboard.month_breakdown[0].approved}</p></div>
                <div className="text-center p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500 uppercase">Processed</p><p className="text-lg font-bold mt-1 text-blue-600">{dashboard.month_breakdown[0].processed}</p></div>
              </>
            ) : <p className="col-span-4 text-center py-4 text-slate-500">No deduction data available</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ========== MAIN PAYROLL PAGE ==========
export default function Payroll() {
  const { selectedEntity, entities } = useOutletContext() || {};
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [myEmployeeId, setMyEmployeeId] = useState(null);
  const [monthFilter, setMonthFilter] = useState(new Date().toISOString().slice(0, 7));

  const userRole = user?.role || 'employee';
  const isEmployee = ['employee', 'hod', 'principal', 'dean'].includes(userRole); // self-only payroll view
  const isAdmin = ['hr', 'admin', 'super_admin', 'finance'].includes(userRole);
  const isFinance = ['finance', 'super_admin'].includes(userRole);
  const canGeneratePayroll = ['hr', 'admin', 'super_admin'].includes(userRole);
  const canApprovePayroll = ['hr', 'admin', 'super_admin', 'institute_admin', 'branch_admin'].includes(userRole);
  // Only Super Admin and Finance Manager can edit individual payroll attributes
  const canEditPayroll = ['super_admin', 'admin', 'hr', 'finance', 'finance_head', 'institute_admin', 'branch_admin'].includes(userRole);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const qs = new URLSearchParams();
        if (selectedEntity) { qs.append('entity_id', selectedEntity.entity_id); qs.append('include_descendants', 'true'); }
        if (monthFilter) qs.append('month', monthFilter);
        const res = await fetch(`${API_URL}/api/employees${qs.toString() ? `?${qs}` : ''}`, { headers: getHeaders(), credentials: 'include' });
        if (res.ok) {
          const emps = await res.json();
          setEmployees(emps);
          if (isEmployee && user?.email) {
            const me = emps.find(e => e.email === user.email);
            if (me) setMyEmployeeId(me.employee_id);
          }
        }
      } catch (err) { console.error('Failed to fetch employees', err); }
    };
    fetchEmployees();
  }, [selectedEntity, isEmployee, user?.email, monthFilter]);

  const defaultTab = isAdmin ? 'records' : 'records';

  return (
    <div className="space-y-5 animate-fade-in" data-testid="payroll-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            {isEmployee ? 'My Salary' : 'Payroll Management'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isEmployee ? 'View salary slips, tax forms, and statutory deductions' : 'Process salaries, generate reports, and manage compliance'}
          </p>
        </div>
        {isAdmin && <ClaSettingsDialog />}
      </div>

      {/* Tabs */}
      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto" data-testid="payroll-tabs">
          <TabsTrigger value="records" data-testid="tab-records">
            <IndianRupee className="w-3.5 h-3.5 mr-1.5" />Records
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Clock className="w-3.5 h-3.5 mr-1.5" />Salary History
          </TabsTrigger>
          <TabsTrigger value="form16" data-testid="tab-form16">
            <FileText className="w-3.5 h-3.5 mr-1.5" />Form 16
          </TabsTrigger>
          <TabsTrigger value="pfesi" data-testid="tab-pfesi">
            <Shield className="w-3.5 h-3.5 mr-1.5" />PF / ESI
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <BarChart3 className="w-3.5 h-3.5 mr-1.5" />Dashboard
            </TabsTrigger>
          )}
          {canGeneratePayroll && (
            <TabsTrigger value="visibility" data-testid="tab-visibility">
              <Eye className="w-3.5 h-3.5 mr-1.5" />Visibility
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="records">
          <PayrollRecordsTab
            selectedEntity={selectedEntity}
            employees={employees}
            isEmployee={isEmployee}
            myEmployeeId={myEmployeeId}
            canGeneratePayroll={canGeneratePayroll}
            canApprovePayroll={canApprovePayroll}
            isFinance={isFinance}
            canEditPayroll={canEditPayroll}
            monthFilter={monthFilter}
            setMonthFilter={setMonthFilter}
          />
        </TabsContent>

        <TabsContent value="history">
          <SalaryHistoryTab myEmployeeId={myEmployeeId} employees={employees} isEmployee={isEmployee} />
        </TabsContent>

        <TabsContent value="form16">
          <Form16Tab myEmployeeId={myEmployeeId} employees={employees} isEmployee={isEmployee} />
        </TabsContent>

        <TabsContent value="pfesi">
          <PFESITab myEmployeeId={myEmployeeId} employees={employees} isEmployee={isEmployee} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="dashboard">
            <PayrollDashboardTab selectedEntity={selectedEntity} entities={entities} />
          </TabsContent>
        )}

        {canGeneratePayroll && (
          <TabsContent value="visibility">
            <PayrollVisibility selectedEntity={selectedEntity} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
